import apiCall from '@/store/apiCall';
import URLS from '@/store/urls';
import notificationWithIcon from '@/utils/notification';

const actions = {
  async getPromoCodes({ commit }, params) {
    try {
      const { data: { data } } = await apiCall({
        type: 'GET',
        url: URLS.promotionCoupons,
        params,
        isToken: true,
      });
      commit('GET_COUPONS_SUCCESS', data);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async searchPromoCodes({ commit }, params) {
    try {
      const { data: { data } } = await apiCall({
        type: 'GET',
        url: URLS.promotionCouponsFts,
        params,
        isToken: true,
      });
      commit('GET_COUPONS_SUCCESS', data);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async getOnePromoCode({ commit }, id) {
    try {
      const { data: { data } } = await apiCall({
        type: 'GET',
        url: `${URLS.promotionCoupons}/${id}`,
        isToken: true,
      });
      commit('GET_ONE_PROMO_CODE_SUCCESS', data);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async deletePromoCode({ dispatch }, payload) {
    const { id, params } = payload;
    try {
      await apiCall({
        type: 'DELETE',
        url: `${URLS.promotionCoupons}/${id}`,
        isToken: true,
      });
      notificationWithIcon('success', 'Promo code deleted');
      dispatch('findPromoCodes', params);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async createPromoCode({ dispatch }, payload) {
    const { promoCode, params } = payload;
    const { promoCodeDetails, selectedCategories } = promoCode;
    try {
      const { data: { data } } = await apiCall({
        type: 'POST',
        url: URLS.promotionCoupons,
        body: promoCodeDetails,
        isToken: true,
      });
      notificationWithIcon('success', 'Promo code created');
      dispatch('searchPromoCodes', params);
      if (selectedCategories && selectedCategories.length) {
        const values = {
          selectedCategories,
          promoCodeId: data.id,
        };
        dispatch('addPromoCodeInCategories', values);
      }
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async editPromoCode({ dispatch }, payload) {
    const {
      promoCode,
      params,
      promoCodeId,
      intervals,
      categories,
    } = payload;
    const { promoCodeDetails, selectedCategories } = promoCode;
    try {
      await apiCall({
        type: 'PUT',
        url: `${URLS.promotionCoupons}/${promoCodeId}`,
        body: promoCodeDetails,
        isToken: true,
      });
      await apiCall({
        type: 'PUT',
        url: `${URLS.promotionCoupons}/${promoCodeId}/intervals`,
        body: intervals,
        isToken: true,
      });
      notificationWithIcon('success', 'Promo code edited');
      dispatch('searchPromoCodes', params);
      const valuesForAdd = {
        selectedCategories,
        promoCodeId,
      };
      if (categories && categories.length) {
        const valuesForDeleted = {
          categories,
          promoCodeId,
        };
        const values = {
          valuesForDeleted,
          valuesForAdd,
        };
        dispatch('deletePromoCodeInCategories', values);
      } else if (selectedCategories && selectedCategories.length) {
        dispatch('addPromoCodeInCategories', valuesForAdd);
      }
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async getPromoTemplates({ commit }) {
    try {
      const params = { offset: 0, limit: 9999 };
      const { data: { data } } = await apiCall({
        type: 'GET',
        url: URLS.promotionTemplates,
        params,
        isToken: true,
      });
      commit('GET_TEMPLATES_SUCCESS', data);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async createPromoTemplates({ dispatch }, payload) {
    try {
      await apiCall({
        type: 'POST',
        url: URLS.promotionTemplates,
        body: { ...payload, dateCreated: new Date() },
        isToken: true,
      });
      notificationWithIcon('success', 'Template created');
      dispatch('getPromoTemplates');
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async editPromoTemplates({ dispatch }, payload) {
    try {
      const {
        id,
        bannerId,
        template,
      } = payload;
      delete template.id;
      delete template.addedAt;
      delete template.updatedAt;
      const {
        banner,
        steps,
        ...newTemplate
      } = template;
      delete banner.id;
      delete banner.addedAt;
      delete banner.updatedAt;
      delete banner.templateId;
      await Promise.all(steps.map(({ id: itemId, ...item }) => apiCall({
        type: 'PUT',
        url: `${URLS.promotionTemplates}/content/${itemId}`,
        body: item,
        isToken: true,
      })));
      await Promise.all([
        apiCall({
          type: 'PUT',
          url: `${URLS.promotionTemplates}/${id}`,
          body: newTemplate,
          isToken: true,
        }),
        apiCall({
          type: 'PUT',
          url: `${URLS.promotionTemplates}/banner/${bannerId}`,
          body: banner,
          isToken: true,
        }),
      ]);
      notificationWithIcon('success', 'Template edited');
      dispatch('getPromoTemplates');
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async selectBannerTemplate({ commit }, payload) {
    try {
      commit('SELECT_BANNER_TEMPLATE_SUCCESS', payload);
    } catch (err) {
      const { response: { data: { message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async changeStyleTemplate({ dispatch }, payload) {
    const { id, ...rest } = payload;
    try {
      await apiCall({
        type: 'PUT',
        url: `${URLS.promotionTemplates}/${id}`,
        body: rest,
        isToken: true,
      });
      notificationWithIcon('success', 'Css styles saved');
      dispatch('getPromoTemplates');
    } catch (err) {
      const { response: { data: { message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async changeActiveTemplate({ dispatch }, payload) {
    const { id, params } = payload;
    try {
      await apiCall({
        type: 'PUT',
        url: `${URLS.promotionTemplates}/${id}`,
        body: params,
        isToken: true,
      });
      notificationWithIcon('success', 'Template\'s  active changed');
      dispatch('getPromoTemplates');
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  // async copyBannerTemplate({ dispatch }, template) {
  //   try {
  //     const { steps } = template;
  //     console.log('=>(actions.js:252) template', template);
  //     const stepsWithImages = await Promise.all(
  //       steps.map(async (step, index) => {
  //         const {
  //           desktopImage,
  //           mobileImage,
  //         } = step;
  //         console.log('=>(actions.js:259) desktopImage', desktopImage);
  //         console.log('=>(actions.js:259) mobileImage', mobileImage);
  //         if (desktopImage) {
  //           const [
  //             { data: { data: { url } } },
  //             { data: { data: { url: mobileUrl } } },
  //           ] = await Promise.all(
  //             [
  //               apiCall({
  //                 type: 'POST',
  //                 headers: { 'content-type': 'multipart/form-data' },
  //                 url: URLS.fileUpload,
  //                 data: await urlToFormData(desktopImage, 'promotions'),
  //                 isToken: true,
  //               }),
  //               apiCall({
  //                 type: 'POST',
  //                 headers: { 'content-type': 'multipart/form-data' },
  //                 url: URLS.fileUpload,
  //                 data: await urlToFormData(mobileImage, 'promotions'),
  //                 isToken: true,
  //               }),
  //             ],
  //           );
  //           console.log('=>(actions.js:279) url', url);
  //           console.log('=>(actions.js:279) mobileUrl', mobileUrl);
  //           return {
  //             ...step,
  //             name: `Step ${index + 1}`,
  //             desktopImage: url,
  //             mobileImage: mobileUrl,
  //           };
  //         }
  //         return {
  //           ...step,
  //           name: `Step ${index + 1}`,
  //           desktopImage: null,
  //           mobileImage: null,
  //         };
  //       }),
  //     );
  //     console.log('=>(actions.js:288) stepsWithImages', stepsWithImages);
  //     const newTemplate = {
  //       ...template,
  //       name: `${template.name} (copy)`,
  //       dateCreated: new Date(),
  //       steps: stepsWithImages,
  //     };
  //     await apiCall({
  //       type: 'POST',
  //       url: URLS.promotionTemplates,
  //       body: newTemplate,
  //       isToken: true,
  //     });
  //     notificationWithIcon('success', 'Template copied');
  //     dispatch('getPromoTemplates');
  //   } catch (err) {
  //     console.log('=>(actions.js:630) err', err);
  //     const { response: { data: { message } } = { data: { message: err.message } } } = err;
  //     notificationWithIcon('error', message);
  //   }
  // },
  async copyBannerTemplate({ dispatch }, template) {
    try {
      const { steps } = template;
      const stepsWithoutImages = steps.map((step, index) => ({
        ...step,
        name: `Step ${index + 1}`,
        desktopImage: null,
        mobileImage: null,
      }));
      const newTemplate = {
        ...template,
        name: `${template.name} (copy)`,
        dateCreated: new Date(),
        steps: stepsWithoutImages,
      };
      await apiCall({
        type: 'POST',
        url: URLS.promotionTemplates,
        body: newTemplate,
        isToken: true,
      });
      notificationWithIcon('success', 'Template copied');
      dispatch('getPromoTemplates');
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async deleteTemplate({ dispatch }, id) {
    try {
      await apiCall({
        type: 'DELETE',
        url: `${URLS.promotionTemplates}/${id}`,
        isToken: true,
      });
      notificationWithIcon('success', 'Template deleted');
      dispatch('getPromoTemplates');
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async getPromoCategories({ commit }, params) {
    try {
      const { data: { data } } = await apiCall({
        type: 'GET',
        url: URLS.promoCategories,
        params,
        isToken: true,
      });
      commit('GET_PROMO_CATEGORIES_SUCCESS', data);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async createCategory({ dispatch }, payload) {
    const { value, params } = payload;
    try {
      await apiCall({
        type: 'POST',
        url: URLS.promoCategories,
        body: value,
        isToken: true,
      });
      notificationWithIcon('success', 'Promo category created');
      dispatch('getPromoCategories', params);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async editCategory({ dispatch }, payload) {
    const { id, value, params } = payload;
    try {
      await apiCall({
        type: 'PUT',
        url: `${URLS.promoCategories}/${id}`,
        body: value,
        isToken: true,
      });
      notificationWithIcon('success', 'Category edited');
      dispatch('getPromoCategories', params);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async deleteCategory({ dispatch }, payload) {
    const { id, params } = payload;
    try {
      await apiCall({
        type: 'DELETE',
        url: `${URLS.promoCategories}/${id}`,
        isToken: true,
      });
      notificationWithIcon('success', 'Forever Club Card deleted');
      dispatch('getPromoCategories', params);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async addPromoCodeInCategories(_, payload) {
    const { selectedCategories, promoCodeId } = payload;
    try {
      await Promise.all(selectedCategories.map((id) => apiCall({
        type: 'POST',
        url: URLS.promoInCategory(id, promoCodeId),
        isToken: true,
      })));
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async deletePromoCodeInCategories({ dispatch }, values) {
    const { valuesForDeleted, valuesForAdd } = values;
    const { categories, promoCodeId } = valuesForDeleted;
    try {
      await Promise.all(categories.map((id) => apiCall({
        type: 'DELETE',
        url: URLS.promoInCategory(id, promoCodeId),
        isToken: true,
      })));
      if (valuesForAdd) {
        dispatch('addPromoCodeInCategories', valuesForAdd);
      }
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async getForeverClubCards({ commit }, payload) {
    try {
      const { data: { data } } = await apiCall({
        type: 'GET',
        url: URLS.foreverClubCard,
        params: payload,
        isToken: true,
      });
      commit('GET_FOREVER_CLUB_CARDS_SUCCESS', data);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async createForeverClubCard({ dispatch }, payload) {
    const {
      value,
      limit,
      offset,
      sortingParams,
    } = payload;
    try {
      await apiCall({
        type: 'POST',
        url: URLS.foreverClubCard,
        body: value,
        isToken: true,
      });
      notificationWithIcon('success', 'Forever Club Card created');
      dispatch('getForeverClubCards', { limit, offset, sortingParams });
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async getForeverClubCardInfo({ commit }, id) {
    try {
      const { data: { data } } = await apiCall({
        type: 'GET',
        url: `${URLS.foreverClubCard}/${id}`,
        isToken: true,
      });
      commit('GET_FOREVER_CLUB_CARD_INFO_SUCCESS', data);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async deleteForeverClubCard({ dispatch }, payload) {
    const {
      id, limit, offset, sortingParams,
    } = payload;
    try {
      await apiCall({
        type: 'DELETE',
        url: `${URLS.foreverClubCard}/${id}`,
        isToken: true,
      });
      notificationWithIcon('success', 'Forever Club Card deleted');
      dispatch('getForeverClubCards', { limit, offset, sortingParams });
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async getPromoSections({ commit }, value) {
    try {
      const { data: { data } } = await apiCall({
        type: 'GET',
        url: `${URLS.promoSections}/period`,
        params: value,
        isToken: true,
      });
      commit('GET_PROMO_SECTIONS_SUCCESS', data.data);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async copyPromoSections({ dispatch }, { copy, promoSection }) {
    try {
      await apiCall({
        type: 'POST',
        url: `${URLS.promoSections}/copy-intervals`,
        body: copy,
        isToken: true,
      });
      notificationWithIcon('success', 'Promo section copied');
      dispatch('getPromoSections', promoSection);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async createPromoAd({ dispatch }, { value, promosectionsParams, resetData }) {
    try {
      const stepsImages = value.steps
        .reduce((acc, step) => [...acc, step.desktopImage, step.mobileImage], []);

      const uploadedImages = await Promise.all(stepsImages.map(async (image) => {
        if (image && image.length > 0 && image[0].file) {
          const formData = new FormData();
          formData.append('file', image[0].file);
          formData.append('folder', 'default');
          return apiCall({
            type: 'POST',
            headers: { 'content-type': 'multipart/form-data' },
            url: URLS.fileUpload,
            body: formData,
            isToken: true,
          });
        }
        return { data: { data: { url: image?.[0]?.url || '' } } };
      }));
      const newValue = {
        ...value,
        steps: value.steps.map((step, index) => {
          if (index === 0) {
            return {
              ...step,
              desktopImage: uploadedImages[0].data.data.url,
              mobileImage: uploadedImages[1].data.data.url,
            };
          }
          if (index === 1) {
            return {
              ...step,
              desktopImage: uploadedImages[2].data.data.url,
              mobileImage: uploadedImages[3].data.data.url,
            };
          }
          return {
            ...step,
            desktopImage: uploadedImages[4].data.data.url,
            mobileImage: uploadedImages[5].data.data.url,
          };
        }),
      };
      await apiCall({
        type: 'POST',
        url: `${URLS.promoSections}`,
        body: newValue,
        isToken: true,
      });
      notificationWithIcon('success', 'Promo ad created successfully');
      dispatch('getPromoSections', promosectionsParams);
      if (resetData) {
        resetData();
      }
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async updatePromoSection(_, { id, promo }) {
    try {
      await apiCall({
        type: 'PUT',
        url: `${URLS.promoSections}/${id}`,
        body: promo,
        isToken: true,
      });
      notificationWithIcon('success', 'Promo ad main data updated successfully');
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async updatePromoSectionBanner(_, { id, banner, resetData }) {
    try {
      await apiCall({
        type: 'PUT',
        url: `${URLS.promoSections}/banner/${id}`,
        body: banner,
        isToken: true,
      });
      notificationWithIcon('success', 'Promo ad banner data updated successfully');
      if (resetData) {
        resetData();
      }
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async addNewDatesForPromoAd(_, dates) {
    try {
      await apiCall({
        type: 'POST',
        url: `${URLS.promoSections}/intervals`,
        body: dates,
        isToken: true,
      });
      notificationWithIcon('success', 'New dates added to promo ad successfully');
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async deletePromoSectionDateInterval(_, payload) {
    try {
      await apiCall({
        type: 'DELETE',
        url: `${URLS.promoSections}/intervals/${payload}`,
        isToken: true,
      });
      notificationWithIcon('success', 'Date delete successfully');
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
};

export default actions;
